import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Chip,
  Alert,
  IconButton,
  Paper,
  TextField,
  InputAdornment,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faSync, faExclamationTriangle, faCheck, faCog, faEye, faCheckCircle, faTrash, faInfoCircle, faCalendarAlt, faBuilding, faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';
import {
  Search,
  Clear,
  FilterList
} from '@mui/icons-material';
import notificationService from '../services/notificationService';
import NotificationList from '../components/notifications/NotificationList';
import NotificationTable from '../components/notifications/NotificationTable';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '@mui/material/styles';
import { estimateLeakLitresPerHour } from '../utils/leakEstimate';
import { useLocation, useNavigate } from 'react-router-dom';

// Sort notifications so the biggest estimated leak comes first (others keep recency order)
const sortByLeakSizeDesc = (list) => {
  return [...(list || [])].sort((a, b) => {
    const ea = estimateLeakLitresPerHour(a);
    const eb = estimateLeakLitresPerHour(b);
    const va = ea == null ? -1 : ea;
    const vb = eb == null ? -1 : eb;
    if (vb !== va) return vb - va;
    const ta = new Date(a?.detectedAt || a?.createdAt || 0).getTime();
    const tb = new Date(b?.detectedAt || b?.createdAt || 0).getTime();
    return tb - ta;
  });
};

const STATUS_FILTERS = ['new', 'viewed', 'resolved'];
const TYPE_FILTERS = ['threshold_exceeded', 'leak_detected', 'signal_strength'];

const Notifications = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const filterDeviceId = (queryParams.get('deviceId') || '').trim();
  const filterDeviceLabel = (queryParams.get('deviceLabel') || '').trim();
  const filterType = (queryParams.get('type') || '').trim();

  const initialTab = TYPE_FILTERS.includes(filterType) ? filterType : 'all';

  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [counts, setCounts] = useState({ total: 0, new: 0, viewed: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [runningDailyThresholds, setRunningDailyThresholds] = useState(false);
  const [runningLeakDaily, setRunningLeakDaily] = useState(false);
  const [runningSignalStrength, setRunningSignalStrength] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [widescreenMode, setWidescreenMode] = useState(false);
  const { user } = useAuth();
  
  // Check if user is admin or operator (operators get admin access but cannot delete)
  const isAdmin = user && user.role === 'admin';
  const isAdminOrOperator = isAdmin || user?.role === 'operator';
  
  // Fetch notifications based on active tab
  const fetchNotifications = useCallback(async (filterValue = activeTab, currentPage = page) => {
    try {
      setLoading(true);
      setError(null);
      
      // Build filter options based on active tab. Fetch a larger page for leaks so the
      // "highest leak first" ordering reflects all leaks rather than a single 20-row page.
      const options = { page: currentPage, limit: filterValue === 'leak_detected' ? 500 : 20 };

      if (filterDeviceId) {
        options.deviceId = filterDeviceId;
      }
      
      // Check if it's a status filter or type filter
      
      if (STATUS_FILTERS.includes(filterValue)) {
        options.status = filterValue;
      } else if (TYPE_FILTERS.includes(filterValue)) {
        options.type = filterValue;
      }
      // 'all' means no filter
      
      const result = await notificationService.getNotifications(options);
      setNotifications(result.notifications);
      setFilteredNotifications(result.notifications);
      setTotalPages(result.totalPages);
      
      // Also update counts
      const countsData = await notificationService.getNotificationCounts();
      setCounts(countsData);
    } catch (err) {
      setError('Failed to load notifications. Please try again later.');
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, filterDeviceId]);
  
  // Handle manual daily thresholds generation
  const handleRunDailyThresholds = async () => {
    try {
      setRunningDailyThresholds(true);
      setError(null);
      const result = await notificationService.runDailyThresholds();
      setSuccessMessage(`Daily thresholds completed. ${result.notificationsCreated} notification(s) created for previous day.`);
      fetchNotifications(activeTab, page);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError('Failed to run daily thresholds. Please try again later.');
      console.error('Error running daily thresholds:', err);
    } finally {
      setRunningDailyThresholds(false);
    }
  };

  // Handle manual leak analysis for previous day
  const handleRunLeakDaily = async () => {
    try {
      setRunningLeakDaily(true);
      setError(null);
      const result = await notificationService.runLeakDaily();
      setSuccessMessage(`Leak analysis completed. ${result.notificationsCreated} notification(s) created for previous day.`);
      fetchNotifications(activeTab, page);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError('Failed to run leak analysis. Please try again later.');
      console.error('Error running leak analysis:', err);
    } finally {
      setRunningLeakDaily(false);
    }
  };

  // Handle manual signal strength check
  const handleRunSignalStrength = async () => {
    try {
      setRunningSignalStrength(true);
      setError(null);
      const result = await notificationService.runSignalStrengthCheck();
      setSuccessMessage(`Signal strength check completed. ${result.notificationsCreated} notification(s) created.`);
      fetchNotifications(activeTab, page);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError('Failed to run signal strength check. Please try again later.');
      console.error('Error running signal strength check:', err);
    } finally {
      setRunningSignalStrength(false);
    }
  };
  
  // Apply search filter to notifications
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredNotifications(notifications);
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = notifications.filter(notification => 
        (notification.message && notification.message.toLowerCase().includes(lowercasedSearch)) ||
        (notification.device?.serialNumber && notification.device.serialNumber.toLowerCase().includes(lowercasedSearch)) ||
        (notification.device?.description && notification.device.description.toLowerCase().includes(lowercasedSearch)) ||
        (notification.site?.description && notification.site.description.toLowerCase().includes(lowercasedSearch)) ||
        (notification.type && notification.type.toLowerCase().includes(lowercasedSearch)) ||
        (notification.status && notification.status.toLowerCase().includes(lowercasedSearch))
      );
      setFilteredNotifications(filtered);
    }
  }, [searchTerm, notifications]);

  // Handle updating notification status
  const handleUpdateStatus = async (id, newStatus) => {
    try {
      // Update status via API
      await notificationService.updateNotificationStatus(id, newStatus);
      
      // Update local state
      const updatedNotifications = notifications.map(notification => 
        notification.id === id 
          ? { ...notification, status: newStatus, resolvedAt: newStatus === 'resolved' ? new Date() : notification.resolvedAt } 
          : notification
      );
      setNotifications(updatedNotifications);
      
      // Update counts
      const countsData = await notificationService.getNotificationCounts();
      setCounts(countsData);
      
      setSuccessMessage(`Notification ${newStatus === 'resolved' ? 'resolved' : 'marked as viewed'} successfully.`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      setError('Failed to update notification status. Please try again later.');
      console.error('Error updating notification status:', err);
    }
  };
  
  // Handle delete notification
  const handleDelete = async (id) => {
    if (!isAdmin) return;
    try {
      setError(null);
      await notificationService.deleteNotification(id);
      
      // Update local state
      setNotifications(notifications.filter(notification => notification.id !== id));
      
      // Update counts
      const countsData = await notificationService.getNotificationCounts();
      setCounts(countsData);
      
      setSuccessMessage('Notification deleted successfully.');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      setError('Failed to delete notification. Please try again later.');
      console.error('Error deleting notification:', err);
    }
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1); // Reset to first page when changing tabs
    setSearchTerm(''); // Clear search when changing tabs
    fetchNotifications(tab, 1); // Fetch for the first page of the new tab
  };

  useEffect(() => {
    if (!isAdminOrOperator) return;
    const nextTab = TYPE_FILTERS.includes(filterType) ? filterType : 'all';
    setActiveTab(nextTab);
    setPage(1);
    setSearchTerm('');
  }, [isAdminOrOperator, filterType, filterDeviceId]);

  // Initial data load
  useEffect(() => {
    if (isAdminOrOperator) {
      fetchNotifications();
    }
  }, [isAdminOrOperator, fetchNotifications]);

  // If user is not admin or operator, show access denied message
  if (!isAdminOrOperator) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" icon={<FontAwesomeIcon icon={faExclamationTriangle} />}>
          Access Denied: You need admin privileges to view this page.
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h2">
          Notifications
        </Typography>
        {isAdminOrOperator && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant={widescreenMode ? 'contained' : 'outlined'}
              onClick={() => setWidescreenMode(!widescreenMode)}
              startIcon={<FontAwesomeIcon icon={widescreenMode ? faCompress : faExpand} />}
              sx={{
                borderColor: '#4caf50',
                color: widescreenMode ? '#fff' : '#4caf50',
                backgroundColor: widescreenMode ? '#4caf50' : 'transparent',
                '&:hover': {
                  borderColor: '#45a049',
                  backgroundColor: widescreenMode ? '#45a049' : 'rgba(76, 175, 80, 0.04)'
                }
              }}
            >
              Widescreen
            </Button>
            <Button
              variant="outlined"
              onClick={handleRunDailyThresholds}
              disabled={runningDailyThresholds}
              startIcon={<FontAwesomeIcon icon={faSync} className={runningDailyThresholds ? 'fa-spin' : ''} />}
              sx={{
                borderColor: '#2196f3',
                color: '#2196f3',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: 'rgba(33, 150, 243, 0.04)'
                }
              }}
            >
              {runningDailyThresholds ? 'Running...' : 'Run Daily Thresholds'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleRunLeakDaily}
              disabled={runningLeakDaily}
              startIcon={<FontAwesomeIcon icon={faSync} className={runningLeakDaily ? 'fa-spin' : ''} />}
              sx={{
                borderColor: '#f44336',
                color: '#f44336',
                '&:hover': {
                  borderColor: '#d32f2f',
                  backgroundColor: 'rgba(244, 67, 54, 0.04)'
                }
              }}
            >
              {runningLeakDaily ? 'Running...' : 'Run Leak Analysis'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleRunSignalStrength}
              disabled={runningSignalStrength}
              startIcon={<FontAwesomeIcon icon={faSync} className={runningSignalStrength ? 'fa-spin' : ''} />}
              sx={{
                borderColor: '#9c27b0',
                color: '#9c27b0',
                '&:hover': {
                  borderColor: '#7b1fa2',
                  backgroundColor: 'rgba(156, 39, 176, 0.04)'
                }
              }}
            >
              {runningSignalStrength ? 'Running...' : 'Run Signal Strength'}
            </Button>
          </Box>
        )}
      </Box>
      
      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      
      {/* Search Filter */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 2, 
          mb: 3,
          position: 'sticky',
          top: 64, // Height of AppBar
          zIndex: theme.zIndex.appBar - 1,
          backgroundColor: 'background.paper',
          boxShadow: theme.shadows[1]
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search notifications..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="medium"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={() => setSearchTerm('')}
                  edge="end"
                  size="small"
                >
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />
        {filterDeviceId && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              color="primary"
              variant="outlined"
              label={`Device filter: ${filterDeviceLabel || filterDeviceId}`}
            />
            <Button
              size="small"
              onClick={() => navigate('/notifications')}
            >
              Clear device filter
            </Button>
          </Box>
        )}
      </Paper>

      {/* Notification counts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" color="text.secondary">Total</Typography>
                <Typography variant="h3">{counts.total}</Typography>
              </Box>
              <Box sx={{ color: theme.palette.primary.main }}>
                <FontAwesomeIcon icon={faBell} size="2x" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ 
            height: '100%', 
            borderRadius: 2,
            borderLeft: `4px solid ${theme.palette.error.main}`
          }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" color="text.secondary">New</Typography>
                <Typography variant="h3">{counts.new}</Typography>
              </Box>
              <Box sx={{ color: theme.palette.error.main }}>
                <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ 
            height: '100%', 
            borderRadius: 2,
            borderLeft: `4px solid ${theme.palette.warning.main}`
          }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" color="text.secondary">Viewed</Typography>
                <Typography variant="h3">{counts.viewed}</Typography>
              </Box>
              <Box sx={{ color: theme.palette.warning.main }}>
                <FontAwesomeIcon icon={faEye} size="2x" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ 
            height: '100%', 
            borderRadius: 2,
            borderLeft: `4px solid ${theme.palette.success.main}`
          }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" color="text.secondary">Resolved</Typography>
                <Typography variant="h3">{counts.resolved}</Typography>
              </Box>
              <Box sx={{ color: theme.palette.success.main }}>
                <FontAwesomeIcon icon={faCheck} size="2x" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Tabs for notification filtering - only show when not in widescreen mode */}
      {!widescreenMode && (
        <Paper elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => handleTabChange(newValue)}
              aria-label="notification tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="All Notifications" value="all" />
              <Tab label="Thresholds" value="threshold_exceeded" />
              <Tab label="Leaks" value="leak_detected" />
              <Tab label="Signal Strength" value="signal_strength" />
              <Tab label="New" value="new" />
              <Tab label="Viewed" value="viewed" />
              <Tab label="Resolved" value="resolved" />
            </Tabs>
          </Box>
          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <LoadingSpinner />
              </Box>
            ) : filteredNotifications.length > 0 ? (
              <NotificationTable
                showDelete={isAdmin}
                notifications={activeTab === 'leak_detected' ? sortByLeakSizeDesc(filteredNotifications) : filteredNotifications}
                onUpdateStatus={handleUpdateStatus}
                onDelete={handleDelete}
                page={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <FontAwesomeIcon icon={faBell} size="3x" style={{ color: theme.palette.text.secondary, marginBottom: theme.spacing(2) }} />
                <Typography variant="h6" gutterBottom>No notifications to display</Typography>
                <Typography variant="body2" color="text.secondary">
                  Notifications will appear here when the system detects important events
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}
      
      {/* Widescreen Mode - 3 columns for different notification types */}
      {widescreenMode && (
        <Box sx={{ width: '100%' }}>
          <Grid container spacing={2}>
            {/* Threshold Notifications */}
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ borderRadius: 2 }}>
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', backgroundColor: 'rgba(33, 150, 243, 0.08)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2196f3', fontSize: '1rem' }}>
                    Threshold Exceeded
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {notifications.filter(n => n.type === 'threshold_exceeded').length} notification(s)
                  </Typography>
                </Box>
                <Box sx={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto', overflowX: 'hidden' }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <LoadingSpinner />
                    </Box>
                  ) : notifications.filter(n => n.type === 'threshold_exceeded').length > 0 ? (
                    <NotificationTable
                      showDelete={isAdmin}
                      notifications={notifications.filter(n => n.type === 'threshold_exceeded')}
                      onUpdateStatus={handleUpdateStatus}
                      onDelete={handleDelete}
                      page={1}
                      totalPages={1}
                      onPageChange={() => {}}
                      compact={true}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No threshold notifications
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
            
            {/* Leak Notifications */}
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ borderRadius: 2 }}>
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', backgroundColor: 'rgba(244, 67, 54, 0.08)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f44336', fontSize: '1rem' }}>
                    Leak Detected
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {notifications.filter(n => n.type === 'leak_detected').length} notification(s)
                  </Typography>
                </Box>
                <Box sx={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto', overflowX: 'hidden' }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <LoadingSpinner />
                    </Box>
                  ) : notifications.filter(n => n.type === 'leak_detected').length > 0 ? (
                    <NotificationTable
                      showDelete={isAdmin}
                      notifications={sortByLeakSizeDesc(notifications.filter(n => n.type === 'leak_detected'))}
                      onUpdateStatus={handleUpdateStatus}
                      onDelete={handleDelete}
                      page={1}
                      totalPages={1}
                      onPageChange={() => {}}
                      compact={true}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No leak notifications
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
            
            {/* Signal Strength Notifications */}
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ borderRadius: 2 }}>
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', backgroundColor: 'rgba(156, 39, 176, 0.08)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#9c27b0', fontSize: '1rem' }}>
                    Signal Strength
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {notifications.filter(n => n.type === 'signal_strength').length} notification(s)
                  </Typography>
                </Box>
                <Box sx={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto', overflowX: 'hidden' }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <LoadingSpinner />
                    </Box>
                  ) : notifications.filter(n => n.type === 'signal_strength').length > 0 ? (
                    <NotificationTable
                      showDelete={isAdmin}
                      notifications={notifications.filter(n => n.type === 'signal_strength')}
                      onUpdateStatus={handleUpdateStatus}
                      onDelete={handleDelete}
                      page={1}
                      totalPages={1}
                      onPageChange={() => {}}
                      compact={true}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No signal strength notifications
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default Notifications;
